import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  useReveal, ServiceHero, StatsRow, ProcessSection, WhatWeDeliver,
  ServiceCaseStudies, ServiceFAQ,
  IndustriesSection, WhyZoneX, ServiceCTA, Footer,
} from './shared/ServicePageSections';

const COLOR = '#CC0040';

const PROCESS = [
  {
    num:'01', title:'Technical Specification',
    desc:'We begin by documenting the complete firmware scope — every peripheral, communication protocol, power mode, data handling requirement, error condition, and OTA strategy. A firmware spec signed off before development begins eliminates scope creep, integration surprises, and redesigns later.',
    points:[
      'Peripheral inventory — sensors, actuators, displays, communication ICs',
      'Protocol requirements — MQTT, HTTP, BLE, LoRa, CoAP, Modbus, etc.',
      'Power mode design — active, sleep, deep-sleep duty cycles',
      'Error handling, watchdog strategy, and OTA update requirements',
    ],
  },
  {
    num:'02', title:'Architecture & RTOS Selection',
    desc:'We select the right RTOS — FreeRTOS, Zephyr, or bare-metal — based on your MCU, real-time requirements, and memory constraints. Task architecture is designed before a line of code is written: task priorities, stack sizes, inter-task communication, and shared resource management.',
    points:[
      'RTOS selection and justification — FreeRTOS, Zephyr, or bare-metal',
      'Task architecture design with priority mapping and stack sizing',
      'Inter-task communication — queues, semaphores, event groups',
      'Memory allocation plan — RAM, flash, heap partitioning',
    ],
  },
  {
    num:'03', title:'Module Development',
    desc:'We build firmware in clean, testable layers: HAL drivers closest to hardware, middleware above them, and application logic at the top. Each module has a well-defined API, is independently testable, and can be reused across future products. Communication protocol stacks are implemented separately from application logic.',
    points:[
      'HAL-level peripheral drivers for all sensors, actuators, and interfaces',
      'Communication stacks — MQTT, BLE, LoRa, HTTP, CoAP, Modbus',
      'Application logic, state machines, and data pipeline implementation',
      'Power management module with sleep/wake scheduling',
    ],
  },
  {
    num:'04', title:'Integration & Testing',
    desc:'We run hardware-in-the-loop testing to validate that each module works correctly with real hardware — not just in simulation. Edge cases are stress-tested: power interruptions, weak signal reconnection, sensor dropouts, and memory boundary conditions. Power profiling identifies optimisation opportunities.',
    points:[
      'Hardware-in-the-loop module integration testing on target hardware',
      'Edge case simulation — power loss recovery, connectivity loss, sensor fault',
      'Power profiling with current measurement across all operating states',
      'Communication stress testing — repeated connect/disconnect, packet loss',
    ],
  },
  {
    num:'05', title:'OTA & Production Flashing Setup',
    desc:'We implement secure over-the-air firmware update with rollback support, so you can push fixes and new features to deployed devices without a field service visit. Production flashing workflows are documented and delivered with binary files ready for your contract manufacturer.',
    points:[
      'Secure OTA firmware update with cryptographic signature verification',
      'Rollback mechanism — automatic revert on failed update detection',
      'Production flashing workflow and JIG specifications for manufacturing',
      'Binary delivery package with versioning and release documentation',
    ],
  },
];

const DELIVERABLES = [
  { title:'Complete Firmware Source Code', desc:'Well-commented, version-controlled source code with full IP ownership transferred to your team — no lock-in.' },
  { title:'Firmware Architecture Document', desc:'Architecture documentation covering module structure, task design, memory allocation plan, and API specifications.' },
  { title:'RTOS Task Design Plan', desc:'Complete task design including priority mapping, stack sizing, inter-task communication strategy, and timing diagrams.' },
  { title:'Peripheral Driver Library', desc:'HAL-level driver library for all peripherals and sensors used in your product, with clean, documented APIs.' },
  { title:'Communication Protocol Implementations', desc:'MQTT, HTTP, CoAP, BLE, LoRa, Zigbee, Modbus — protocol stacks implemented and tested against your connectivity architecture.' },
  { title:'OTA Firmware Update System', desc:'Secure over-the-air update mechanism with cryptographic verification and rollback support — field-ready.' },
  { title:'Power Profiling Report', desc:'Measured current consumption across all operating states with optimisation recommendations and achieved battery life projections.' },
  { title:'Production Flashing Guide & Binaries', desc:'Step-by-step production flashing workflow, JIG specifications, and production-ready binary files for your contract manufacturer.' },
];

const CASE_STUDIES = [
  {
    tag: 'Agriculture',
    title: 'FreeRTOS Firmware for Field Sensor Network',
    metric: '500+ devices, 2-year field life',
    desc: 'Built modular FreeRTOS firmware for a LoRaWAN agricultural sensor — separate tasks for sensor sampling, communication, power management, and OTA update. Achieved the 2-year target battery life in field testing.',
  },
  {
    tag: 'Healthcare',
    title: 'Secure OTA Update for Medical Devices',
    metric: 'Zero bricked devices after rollout',
    desc: 'Designed and implemented a cryptographically signed OTA firmware update system for a deployed fleet of patient monitoring devices — with automatic rollback on failed update detection.',
  },
  {
    tag: 'Industrial',
    title: 'CNC Machine Monitoring Firmware',
    metric: '<1ms sensor read latency',
    desc: 'Bare-metal firmware on STM32 for real-time vibration monitoring of CNC spindles — deterministic interrupt-driven sensor acquisition, RS485 Modbus output, and configurable threshold alerting.',
  },
];

const FAQS = [
  {
    q: 'Which microcontrollers and hardware platforms do you develop firmware for?',
    a: 'We develop firmware for a wide range of microcontrollers including ESP32, STM32, nRF52, RP2040, PIC, AVR, and ARM Cortex-M series. We also work with Linux-based systems including Raspberry Pi, i.MX, and custom Linux SBCs. Platform selection is driven by your product requirements, not our toolchain preferences.',
  },
  {
    q: 'Do you use FreeRTOS, Zephyr, or bare-metal?',
    a: 'The choice depends on your hardware, real-time requirements, and product complexity. We use FreeRTOS for most commercial IoT applications, Zephyr for products targeting broad hardware portability and Bluetooth LE, and bare-metal for highly constrained applications where an RTOS adds unnecessary overhead. We justify the selection in the architecture document before development begins.',
  },
  {
    q: 'Can you add OTA (Over-The-Air) firmware updates to our existing product?',
    a: 'Yes. We can add secure OTA update capability to existing products — implementing a bootloader, update manager, and secure delivery mechanism compatible with your cloud platform. OTA updates with rollback support are standard in all firmware we deliver.',
  },
  {
    q: 'Do we receive the firmware source code, or just the binary?',
    a: 'You receive the complete, well-commented source code with full IP ownership. We also provide build instructions, dependency documentation, and production binary files. There is no ongoing licensing fee or dependency on Zone-X to compile and maintain the firmware after project completion.',
  },
  {
    q: 'Can you integrate firmware with AWS IoT Core, Azure IoT Hub, or a custom MQTT broker?',
    a: 'Yes. We have implemented MQTT, HTTPS, CoAP, WebSocket, and other communication stacks for AWS IoT Core, Azure IoT Hub, Google Cloud IoT, ThingsBoard, and custom MQTT brokers. Cloud connectivity implementation is a standard part of our firmware service.',
  },
];

function HeroVisual() {
  return (
    <img src="/visuals/services/Service 2.png" alt="Embedded Firmware & Software Development" style={{ width:'100%', display:'block' }} />
  );
}

export default function EmbeddedFirmwarePage() {
  const [heroRef, heroVisible] = useReveal();
  return (
    <>
      <Helmet>
        <title>Embedded Firmware Development Company India — Zone-X</title>
        <meta name="description" content="Zone-X builds production-grade embedded firmware for IoT devices in India — RTOS development, OTA updates, low-power optimization, and clean modular firmware architecture." />
        <link rel="canonical" href="https://zone-x.dev/embedded-firmware-development" />
      </Helmet>

      <div ref={heroRef}>
        <ServiceHero
          tag="Services / Embedded Firmware Development"
          breadcrumb="Firmware Development"
          title="Embedded Firmware &"
          titleAccent="Software Development"
          subtitle="Firmware is where the real complexity lives. Any developer can write code that works once in the lab — but IoT firmware must handle power failures, weak signals, memory constraints, OTA updates, and years of unattended operation. Zone-X builds firmware that runs reliably, even when the world gets complicated."
          geo="Embedded firmware development company serving businesses in Bangalore, Karnataka, and across India."
          color={COLOR}
          visible={heroVisible}
          visual={<HeroVisual />}
        />
      </div>

      <StatsRow />
      <FirmwareComplexity color={COLOR} />
      <ProcessSection steps={PROCESS} color={COLOR} />
      <WhatWeDeliver items={DELIVERABLES} color={COLOR} />
      <ServiceCaseStudies studies={CASE_STUDIES} color={COLOR} />
      <ServiceFAQ faqs={FAQS} color={COLOR} />
      <IndustriesSection />
      <WhyZoneX />
      <ServiceCTA />
      <Footer />
    </>
  );
}

function FirmwareComplexity({ color }) {
  const [ref, visible] = useReveal();
  const challenges = [
    'Unreliable connectivity and automatic reconnection logic',
    'Low-power sleep cycles to extend battery life in the field',
    'Real-time sensor data acquisition and deterministic processing',
    'OTA firmware updates without any risk of bricking the device',
    'Secure, encrypted communication with the cloud backend',
    'Graceful recovery from unexpected resets and power loss events',
  ];
  return (
    <section style={{ padding:'0 0 clamp(40px,5vw,64px)' }}>
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
        <div ref={ref} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'clamp(24px,4vw,56px)', background:'rgba(255,255,255,0.8)', border:'1px solid rgba(10,10,20,0.08)', borderRadius:24, padding:'clamp(28px,4vw,52px)', opacity:visible?1:0, transform:visible?'none':'translateY(22px)', transition:'opacity 520ms, transform 520ms' }} className="fw-two-col">
          <div>
            <span style={{ fontFamily:"'Anton', sans-serif", fontSize:10.5, color:color, textTransform:'uppercase' }}>THE REAL COMPLEXITY</span>
            <h2 style={{ fontFamily:"'Anton',sans-serif", fontSize:'clamp(22px,3vw,36px)', lineHeight:1.1, marginTop:8, marginBottom:16, color:'var(--ink)' }}>
              Firmware is where the{' '}
              <span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color:color }}>real complexity lives</span>
            </h2>
            <p style={{ fontSize:15, lineHeight:1.82, color:'var(--ink-soft)' }}>
              Any developer can write code that works once, in the lab, with a full battery and a strong WiFi signal. But embedded firmware for IoT is a different discipline entirely. At Zone-X, our firmware engineers have built production-grade firmware for devices running 24/7 in the field — in farms, factories, hospitals, and homes across India.
            </p>
          </div>
          <div>
            <p style={{ fontSize:13.5, color:'var(--ink)', marginBottom:16, textTransform:'uppercase', fontFamily:"'Anton', sans-serif" }}>Your firmware must handle:</p>
            <ul style={{ listStyle:'none', padding:0, margin:0 }}>
              {challenges.map((ch, i) => (
                <li key={i} style={{ display:'flex', gap:12, alignItems:'flex-start', paddingTop:i>0?12:0, paddingBottom:i<challenges.length-1?12:0, borderBottom:i<challenges.length-1?'1px solid rgba(10,10,20,0.06)':'none', opacity:visible?1:0, transform:visible?'none':'translateY(12px)', transition:`opacity 400ms ${i*50+200}ms, transform 400ms ${i*50+200}ms` }}>
                  <div style={{ width:18, height:18, borderRadius:5, background:`${color}12`, border:`1px solid ${color}28`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5 4.5-4.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <span style={{ fontSize:14, lineHeight:1.65, color:'var(--ink-soft)', fontWeight:500 }}>{ch}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <style>{`@media(max-width:760px){.fw-two-col{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
}
